import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getPapers, incrementPaperDownload } from '../../services/content';
import { downloadFile, getDownloadFilename } from '../../services/download';
import '../../styles/content-mobile.css';

const PastPapers = ({ subjectId = null }) => {
  const { getUserLevel } = useAuth();
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(new Set());

  useEffect(() => {
    const fetchPapers = async () => {
      try {
        const levelId = getUserLevel();
        if (!levelId) {
          setError('Unable to determine your education level');
          return;
        }

        const papersData = await getPapers(levelId, subjectId);
        setPapers(papersData);
      } catch (err) {
        setError('Failed to load past papers. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPapers();
  }, [getUserLevel, subjectId]);

  const handleDownload = async (paper) => {
    if (downloading.has(paper.id)) return;

    try {
      setDownloading(prev => new Set(prev).add(paper.id));
      
      console.log('📄 Paper download requested:', {
        id: paper.id,
        title: paper.title,
        fileUrl: paper.fileUrl,
        fileName: paper.fileName
      });
      
      // Increment download count in Firestore
      await incrementPaperDownload(paper.id);
      
      // Update local download count
      setPapers(prev => prev.map(p => 
        p.id === paper.id 
          ? { ...p, downloadCount: (p.downloadCount || 0) + 1 }
          : p
      ));
      
      // Use the download service
      const filename = getDownloadFilename(paper);
      const result = await downloadFile(paper.fileUrl, filename);
      
      console.log('📥 Download result:', result);
      
      if (!result.success) {
        if (result.fallback) {
          console.log('⚠️ Download used fallback method:', result.fallback);
        } else {
          setError(`Download failed: ${result.error || 'Unknown error'}`);
        }
      }
      
    } catch (err) {
      console.error('💥 Download error:', err);
      setError('Failed to download paper. Please try again.');
    } finally {
      setDownloading(prev => {
        const newSet = new Set(prev);
        newSet.delete(paper.id);
        return newSet;
      });
    }
  };

  const groupPapersByYear = () => {
    const grouped = {};
    papers.forEach(paper => {
      const year = paper.year;
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(paper);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="past-papers">
        <div className="loading-container">
          <div className="loading-spinner">Loading past papers...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="past-papers">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (papers.length === 0) {
    return (
      <div className="past-papers">
        <div className="empty-state">
          <h3>No Past Papers Available</h3>
          <p>Past papers for this subject will be available soon.</p>
        </div>
      </div>
    );
  }

  const groupedPapers = groupPapersByYear();

  return (
    <div className="past-papers">
      <div className="papers-header">
        <h2>Past Examination Papers</h2>
        <p>Download and practice with previous exam papers</p>
      </div>

      <div className="papers-content">
        {Object.entries(groupedPapers)
          .sort(([yearA], [yearB]) => yearB - yearA)
          .map(([year, yearPapers]) => (
            <div key={year} className="year-group">
              <h3 className="year-title">{year} Papers</h3>
              <div className="papers-list">
                {yearPapers.map((paper) => (
                  <button
                    key={paper.id}
                    onClick={() => handleDownload(paper)}
                    className={`paper-button ${downloading.has(paper.id) ? 'loading' : ''}`}
                    disabled={downloading.has(paper.id)}
                  >
                    {downloading.has(paper.id) ? (
                      <span className="loading-text">Downloading...</span>
                    ) : (
                      <>
                        <span className="paper-icon">📋</span>
                        <span className="paper-title">{paper.title}</span>
                        <span className="download-arrow">→</span>
                      </>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default PastPapers;
